import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { Button } from "@/components/Button";
import { ApiError, bookingsApi } from "@/lib/api";
import { useRoom } from "@/hooks/useRooms";
import { useMe, useMemberToken } from "@/hooks/useMemberAuth";
import { formatPriceEur, formatRange } from "@/lib/format";
import type { BookingCreateResponse } from "@/types/api";

const bookingSchema = z.object({
  booking_type: z.string().min(1, "Bitte Buchungstyp wählen"),
  full_name: z.string().min(2, "Bitte vollständigen Namen eintragen"),
  phone: z.string().min(4, "Telefonnummer erforderlich"),
  purpose: z.string().max(500).optional().or(z.literal("")),
  accept_terms: z.literal(true, {
    errorMap: () => ({ message: "Bitte AGB und Datenschutz akzeptieren" }),
  }),
  marketing_opt_in: z.boolean().default(false),
  website: z.literal(""),
});

type BookingFormValues = z.infer<typeof bookingSchema>;

const labelCls = "block text-body-sm font-medium text-ink";
const fieldCls =
  "mt-1 block w-full rounded border border-line bg-surface px-3 py-2 text-body-sm text-ink shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/30 focus:outline-none";
const readonlyCls =
  "mt-1 block w-full rounded border border-line bg-surface-warm px-3 py-2 text-body-sm text-ink-soft";
const errCls = "mt-1 text-caption text-danger";

export function BookingFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const location = useLocation();
  const startIso = params.get("start");
  const endIso = params.get("end");

  const token = useMemberToken();
  const profile = useMe();

  const { data: room } = useRoom(slug);
  const [confirmation, setConfirmation] = useState<BookingCreateResponse | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<BookingFormValues>({
    defaultValues: {
      booking_type: "",
      full_name: "",
      phone: "",
      purpose: "",
      accept_terms: undefined as unknown as true,
      marketing_opt_in: false,
      website: "",
    },
  });

  // Sobald das Profil geladen ist, Name + Telefon vorbelegen.
  useEffect(() => {
    if (profile.data) {
      reset({
        booking_type: getValues("booking_type"),
        full_name: profile.data.full_name ?? "",
        phone: profile.data.phone ?? "",
        purpose: "",
        accept_terms: undefined as unknown as true,
        marketing_opt_in: profile.data.marketing_opt_in,
        website: "",
      });
    }
  }, [profile.data, reset, getValues]);

  // Sobald der Raum geladen ist: erstes buchbares Angebot vorwählen.
  useEffect(() => {
    if (room && room.prices.length > 0) {
      const current = getValues("booking_type");
      if (!room.prices.some((p) => p.slug === current)) {
        setValue("booking_type", room.prices[0].slug);
      }
    }
  }, [room, getValues, setValue]);

  const selectedType = watch("booking_type");
  const selectedOffer = room?.prices.find((p) => p.slug === selectedType);

  const mutation = useMutation<BookingCreateResponse, ApiError, BookingFormValues>({
    mutationFn: (values) => {
      if (!room || !startIso || !endIso) {
        throw new ApiError("Slot fehlt. Bitte erneut aus dem Kalender wählen.", 400, "");
      }
      const parsed = bookingSchema.parse(values);
      return bookingsApi.create({
        room_id: room.id,
        starts_at: startIso,
        ends_at: endIso,
        booking_type: parsed.booking_type,
        full_name: parsed.full_name,
        phone: parsed.phone,
        purpose: parsed.purpose?.toString() || null,
        accept_terms: parsed.accept_terms,
        marketing_opt_in: parsed.marketing_opt_in ?? false,
        website: parsed.website,
      });
    },
    onSuccess: setConfirmation,
  });

  // Login-Pflicht: ohne Token → Login mit Rück-URL.
  if (!token) {
    const nextUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/konto/login?next=${encodeURIComponent(nextUrl)}`} replace />;
  }

  if (!startIso || !endIso) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <p className="mb-4 text-body-sm text-danger">
          Es wurde kein Slot übergeben. Bitte zuerst im Kalender auf dem Raum einen Slot wählen.
        </p>
        <Link to={slug ? `/raum/${slug}` : "/"} className="text-primary underline">
          Zurück zum Raum
        </Link>
      </div>
    );
  }

  if (confirmation) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <p className="mb-3 text-caption font-medium uppercase tracking-widest text-primary">
          Anfrage eingereicht
        </p>
        <h1 className="mb-3 font-display text-h1 uppercase text-primary">Charlie prüft deine Buchung</h1>
        <p className="mb-6 text-lead text-ink-soft">{confirmation.message}</p>
        <div className="flex justify-center gap-4">
          <Link to="/konto/buchungen" className="text-primary underline">
            Meine Buchungen
          </Link>
          <span className="text-ink-muted">·</span>
          <Link to="/" className="text-primary underline">
            Zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  if (profile.isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center text-body-sm text-ink-muted">
        Profil wird geladen…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <Link to={`/raum/${slug}`} className="mb-6 inline-block text-body-sm text-ink-muted hover:text-primary">
        ← Zurück zum Raum
      </Link>
      <h1 className="mb-2 font-display text-h1 uppercase text-primary">Buchungsanfrage</h1>
      <p className="mb-8 text-body text-ink-soft">
        Erst nach Charlies Freigabe ist deine Buchung fix. Du bekommst die Bestätigung per Email
        inklusive Kalender-Datei.
      </p>
      {room && (
        <div className="mb-8 rounded-card border border-line bg-surface-warm p-5">
          <p className="text-caption uppercase tracking-wider text-ink-muted">{room.name}</p>
          <p className="font-display text-h4 text-ink">{formatRange(startIso, endIso)}</p>
          <p className="mt-1 text-body-sm text-ink-soft">
            {selectedOffer
              ? selectedOffer.price_cents === 0
                ? `${selectedOffer.name} kostenlos`
                : `${formatPriceEur(selectedOffer.price)} / Stunde`
              : "Bitte Buchungstyp wählen"}
          </p>
        </div>
      )}

      {profile.data && (
        <div className="mb-6 rounded-card border border-line bg-surface p-4 text-body-sm text-ink-soft">
          Buchung läuft unter{" "}
          <span className="font-semibold text-ink">{profile.data.email}</span>.{" "}
          <Link to="/konto/profil" className="text-primary underline">
            Profil bearbeiten
          </Link>
        </div>
      )}

      <form
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        className="space-y-5"
        noValidate
      >
        <fieldset>
          <legend className={labelCls}>Buchungstyp</legend>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(room?.prices ?? []).map((offer) => {
              const isActive = selectedType === offer.slug;
              const priceLabel =
                offer.price_cents === 0
                  ? "kostenlos"
                  : `${formatPriceEur(offer.price)} /h`;
              return (
                <label
                  key={offer.slug}
                  className={`cursor-pointer rounded-card border p-3 text-center transition-colors ${
                    isActive
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-line bg-surface text-ink hover:border-primary/40"
                  }`}
                >
                  <input
                    type="radio"
                    value={offer.slug}
                    {...register("booking_type")}
                    className="sr-only"
                  />
                  <span className="block font-display text-h4 uppercase">
                    {offer.name}
                  </span>
                  <span className="block text-caption text-ink-muted">{priceLabel}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
        <div>
          <label htmlFor="full_name" className={labelCls}>
            Vollständiger Name
          </label>
          <input
            id="full_name"
            autoComplete="name"
            {...register("full_name")}
            className={fieldCls}
          />
          {errors.full_name && <p className={errCls}>{errors.full_name.message}</p>}
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="email" className={labelCls}>
              Email <span className="text-ink-muted">(aus deinem Konto)</span>
            </label>
            <input
              id="email"
              type="email"
              value={profile.data?.email ?? ""}
              readOnly
              tabIndex={-1}
              className={readonlyCls}
            />
          </div>
          <div>
            <label htmlFor="phone" className={labelCls}>
              Telefon
            </label>
            <input
              id="phone"
              type="tel"
              autoComplete="tel"
              {...register("phone")}
              className={fieldCls}
            />
            {errors.phone && <p className={errCls}>{errors.phone.message}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="purpose" className={labelCls}>
            Anlass / Hinweise <span className="text-ink-muted">(optional)</span>
          </label>
          <textarea
            id="purpose"
            rows={3}
            {...register("purpose")}
            className={fieldCls}
          />
        </div>

        {/* Honeypot — visuell versteckt, bots füllen aus */}
        <div className="hidden" aria-hidden>
          <label htmlFor="website">Website</label>
          <input id="website" tabIndex={-1} autoComplete="off" {...register("website")} />
        </div>

        <fieldset className="space-y-3 rounded-card border border-line bg-surface p-4">
          <label className="flex items-start gap-3 text-body-sm text-ink">
            <input
              type="checkbox"
              {...register("accept_terms")}
              className="mt-1 h-4 w-4 rounded border-line text-primary focus:ring-primary"
            />
            <span>
              Ich akzeptiere{" "}
              <a href="/agb" target="_blank" rel="noreferrer" className="text-primary underline">
                AGB
              </a>{" "}
              und{" "}
              <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-primary underline">
                Datenschutz
              </a>
              .
            </span>
          </label>
          {errors.accept_terms && <p className={errCls}>{errors.accept_terms.message}</p>}

          <label className="flex items-start gap-3 text-body-sm text-ink-soft">
            <input
              type="checkbox"
              {...register("marketing_opt_in")}
              className="mt-1 h-4 w-4 rounded border-line text-primary focus:ring-primary"
            />
            <span>
              Ich möchte gelegentlich Infos zu neuen Slots und Tanzraum-Angeboten erhalten.
              Jederzeit abbestellbar.
            </span>
          </label>
        </fieldset>

        {mutation.error && (
          <div className="rounded-card border border-danger bg-danger/5 p-3 text-body-sm text-danger">
            {mutation.error.message}
          </div>
        )}

        <Button type="submit" size="lg" loading={isSubmitting || mutation.isPending}>
          Buchungsanfrage absenden
        </Button>
        <p className="text-caption text-ink-muted">
          Nach Klick prüft Charlie deine Anfrage. Die Bestätigung kommt per Email inklusive
          Kalender-Datei.
        </p>
      </form>
    </div>
  );
}
